import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Avatar, Button, CampaignCard, Icon, LoadingIcon, Typography } from '@/primer/components';
import { HeroSection, PageColumn, PageGrid } from '@/components/layout';
import {
  useCampaigns,
  useMemberInfo,
  useMemberProfilePicture,
  useIsProviderConnected,
} from '@/api';
import { parseDateMs } from '@/utils';
import { deriveCampaignCardState } from './Dashboard.utils';

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="col-span-12 flex flex-col items-center text-center py-10 gap-3">
      <Typography variant="header" size="h3">
        No active campaigns... yet.
      </Typography>
      <Typography variant="body" size="medium" color="bodylighter">
        You'll be invited to join a campaign as soon as a brand matches your social profile. Keep an
        eye on your inbox!
      </Typography>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Dashboard() {
  const navigate = useNavigate();

  // ── Data ─────────────────────────────────────────────────────────────────

  const { data: memberInfo, isLoading: isLoadingMember } = useMemberInfo();
  const { data: profile, isLoading: isLoadingProfile } = useMemberProfilePicture(memberInfo?.id);

  const {
    isConnected: isInstagramConnected,
    username: instagramUsername,
    isLoading: isLoadingInstagram,
  } = useIsProviderConnected('INSTAGRAM');

  const {
    data: campaignResponse,
    isLoading: isLoadingCampaigns,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useCampaigns();

  // ── Derived state ─────────────────────────────────────────────────────────

  const isLoadingHeader = isLoadingMember || isLoadingProfile || isLoadingInstagram;

  const displayName =
    isInstagramConnected && instagramUsername
      ? `@${instagramUsername}`
      : (profile?.shortName ?? memberInfo?.firstName ?? '');

  const allCampaigns = campaignResponse?.results ?? [];

  // Schedules a client-side re-render at each invitation expiry deadline in turn
  // so the campaign card badges update without requiring a manual page refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const currentTime = Date.now();

    const campaigns = campaignResponse?.results ?? [];
    const nextExpiry = campaigns.reduce<number | null>((earliest, c) => {
      const ms = c.invitationExpiresAt ? parseDateMs(c.invitationExpiresAt) : null;
      if (ms === null || ms <= currentTime) return earliest;
      return earliest === null ? ms : Math.min(earliest, ms);
    }, null);
    if (nextExpiry === null) return;
    const id = setTimeout(() => setNow(Date.now()), nextExpiry - currentTime);
    return () => clearTimeout(id);
  }, [campaignResponse, now]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Zone 1: Hero header ───────────────────────────────────────────── */}
      {/*
        HeroSection handles:
        - Horizontal bleed (-mx-[var(--spacing-5)] escapes AppShell <main>'s px-5 edge margins)
        - Vertical bleed behind fixed AppHeader on desktop (negative top margin + padding compensation)
        TODO: Replace explicit hex gradient colours with design system tokens
        once a purple-to-peach gradient token is added to the palette.
        Current: from-[#c8b4e8] (soft purple) to-[#f2cfc4] (soft peach)
      */}
      <HeroSection className="bg-gradient-brand flex flex-col gap-3">
        <PageGrid className="px-5">
          <PageColumn className="pt-8 pb-4 flex flex-col items-center gap-3">
            {/* Avatar + username — stacked on mobile, inline on tablet+ */}
            <div className="flex flex-col items-center gap-3 md:flex-row">
              {/* Avatar */}
              {isLoadingHeader ? (
                <div
                  className="size-10 rounded-full bg-disabled animate-pulse"
                  data-testid="avatar-skeleton"
                />
              ) : (
                <Avatar src={profile?.profileImageUrl} fallbackIcon="user" size="large" />
              )}

              {/* Username */}
              {isLoadingHeader ? (
                <div
                  className="h-7 w-[calc(var(--spacing-8)+var(--spacing-8))] rounded bg-disabled animate-pulse"
                  data-testid="username-skeleton"
                />
              ) : (
                <Typography variant="header" size="h2" color="body">
                  {displayName}
                </Typography>
              )}
            </div>

            {/* Connect your socials banner — hidden when Instagram is connected */}
            {!isLoadingInstagram && !isInstagramConnected && (
              <button
                type="button"
                onClick={() => navigate({ to: '/settings/connect' })}
                className="w-full bg-surface rounded-medium border border-default flex items-center gap-3 px-4 py-3 cursor-pointer"
                aria-label="Connect your socials"
              >
                <Icon name="instagram" size="large" color="default" />
                <div className="flex-1 text-left">
                  <Typography
                    variant="body"
                    size="medium"
                    color="header"
                    as="span"
                    className="block"
                  >
                    Connect your socials
                  </Typography>
                  <Typography
                    variant="body"
                    size="small"
                    color="bodylighter"
                    as="span"
                    className="block"
                  >
                    Required for brand partnerships
                  </Typography>
                </div>
                <Icon name="chevron-right" size="medium" color="default" />
              </button>
            )}
          </PageColumn>
        </PageGrid>
      </HeroSection>

      {/* ── Zone 2: Campaign grid ─────────────────────────────────────────── */}
      <PageGrid className="px-5">
        <PageColumn className="pb-4 flex flex-col gap-4">
          <Typography variant="header" size="h1" as="h1">
            Your Campaigns
          </Typography>
          {isLoadingCampaigns ? (
            <div className="flex justify-center py-8" data-testid="campaigns-loading">
              <LoadingIcon size="xl" />
            </div>
          ) : campaignsError ? (
            <div className="flex flex-col items-center py-8 gap-4">
              <Typography variant="body" size="medium" color="error">
                Failed to load campaigns
              </Typography>
              <Button variant="secondary" onClick={() => refetchCampaigns()}>
                Retry
              </Button>
            </div>
          ) : allCampaigns.length === 0 ? (
            <EmptyState />
          ) : (
            // Plain CSS grid inside PageColumn — 1-up on mobile, 2-up on tablet+.
            // gap-5 = var(--spacing-5) = 24px, consistent with grid-primer gutter.
            // Do NOT use grid-primer here — it would add unwanted padding-inline
            // inside the already-constrained PageColumn.
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {allCampaigns.map((campaign) => {
                const {
                  isAvailable,
                  needsToSelectOrClaim,
                  isCompleted,
                  isClaimedAndParticipating,
                  ...cardProps
                } = deriveCampaignCardState(campaign, now);
                return (
                  <CampaignCard
                    key={campaign.id}
                    {...cardProps}
                    onViewProposal={() => {
                      if (isAvailable || needsToSelectOrClaim) {
                        void navigate({
                          to: '/campaign/$id/select-product',
                          params: { id: campaign.id },
                        });
                      } else if (isCompleted || isClaimedAndParticipating) {
                        void navigate({
                          to: '/campaign/$id/tasks',
                          params: { id: campaign.id },
                        });
                      } else {
                        void navigate({
                          to: '/campaign/$id',
                          params: { id: campaign.id },
                        });
                      }
                    }}
                    as="article"
                    shadow="ground"
                  />
                );
              })}
            </div>
          )}
        </PageColumn>
      </PageGrid>
    </>
  );
}

export default Dashboard;
